import type {
  LegacyTweetResponseType,
  LegacyTwitterResponseType,
  MediaIncludeType,
  UserIncludeType
} from '../types';
import type { TweetTypesListing } from './checkTypeOfTweet';
import { checkTypeOfTweet } from './checkTypeOfTweet';
import { getRetweetMedia } from './getRetweetMedia';

type OmittedTweetResponseType = Omit<
  LegacyTweetResponseType,
  'author_id' | 'attachments' | 'edit_history_tweet_ids'
>;

export type NormalizedTweetType = OmittedTweetResponseType & {
  retweet: boolean;
  user: UserIncludeType;
  media: MediaIncludeType[];
};

export function formatTwitterResponse(
  response: LegacyTwitterResponseType,
  tweetTypes: TweetTypesListing
): NormalizedTweetType[] {
  const { data = [], includes } = response;
  const { users = [], media = [], tweets: retweets = [] } = includes ?? {};

  return data.reduce<NormalizedTweetType[]>((acc, tweet) => {
    const currentUser = users.find((user) => user.id === tweet.author_id)!;
    const currentMediaItems = media.filter((mediaItem) =>
      tweet.attachments?.media_keys?.includes(mediaItem.media_key)
    );

    const currentRetweet = retweets.find(
      (retweet) => tweet.referenced_tweets?.[0].id === retweet.id
    );
    const currentRetweetUser = users.find(
      (user) => user.id === currentRetweet?.author_id
    );

    const currentRetweetMediaItems = getRetweetMedia({
      currentRetweet,
      media
    }) as MediaIncludeType[];

    let normalizedTweet: NormalizedTweetType;

    // Ретвит более приоритетен
    if (currentRetweet && currentRetweetUser) {
      normalizedTweet = {
        retweet: true,
        id: currentRetweet.id,
        created_at: currentRetweet.created_at,
        entities: currentRetweet.entities,
        public_metrics: currentRetweet.public_metrics,
        text: currentRetweet.text,
        in_reply_to_user_id: currentRetweet.in_reply_to_user_id,
        referenced_tweets: currentRetweet.referenced_tweets,
        user: { ...currentRetweetUser },
        media: [...currentRetweetMediaItems]
      };
    } else {
      normalizedTweet = {
        retweet: false,
        id: tweet.id,
        created_at: tweet.created_at,
        entities: tweet.entities,
        public_metrics: tweet.public_metrics,
        text: tweet.text,
        in_reply_to_user_id: tweet.in_reply_to_user_id,
        referenced_tweets: tweet.referenced_tweets,
        user: { ...currentUser },
        media: [...currentMediaItems]
      };
    }

    if (checkTypeOfTweet(normalizedTweet, tweetTypes)) {
      acc.push(normalizedTweet);
    }

    return acc;
  }, []);
}
